import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  getGridStringOperators,
  getGridNumericOperators,
  getGridDateOperators,
  getGridBooleanOperators,
} from "@mui/x-data-grid";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import MultiSelectValueEditor from "./MultiSelectValueEditor";

// Custom multi-filter panel slotted into the DataGrid via slots.filterPanel.
// Replaces the community grid's single-row built-in panel.
//
// Props (passed via slotProps.filterPanel):
//   columns: GridColDef[]                      all grid columns
//   apiRef: GridApiRef                          grid api
//   applyFiltersCallback: (model) => void       parent's applyFilters
//   onClose: () => void                         hide the panel
//
// Each row: { id, field, operator, value }
// On Apply: build { items: [...], logicOperator: 'and' }, call
//   apiRef.current.setFilterModel(model), applyFiltersCallback(model), onClose().

let rowIdSeq = 0;
const nextRowId = () => `mf-${++rowIdSeq}`;

const emptyRow = () => ({ id: nextRowId(), field: "", operator: "", value: "" });

// Single-value editor for non-isanyof operators.
const SingleValueEditor = ({ col, value, onChange }) => {
  const valueOptions = typeof col?.valueOptions === "function" ? col.valueOptions() : col?.valueOptions;

  if (Array.isArray(valueOptions) && valueOptions.length > 0) {
    const opts = valueOptions.map((o) =>
      typeof o === "object" && o !== null ? { value: String(o.value), label: o.label ?? String(o.value) } : { value: String(o), label: String(o) }
    );
    return (
      <FormControl size="small" fullWidth>
        <InputLabel>{col.headerName || col.field}</InputLabel>
        <Select
          label={col.headerName || col.field}
          value={value == null ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
        >
          <MenuItem value=""><em>—</em></MenuItem>
          {opts.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }
  return (
    <TextField
      size="small"
      fullWidth
      label={col?.headerName || col?.field}
      value={value == null ? "" : value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

const MultiFilterPanel = ({ columns, apiRef, applyFiltersCallback, onClose }) => {
  const filterableCols = useMemo(
    () => (columns || []).filter((c) => c && c.field && c.filterable !== false),
    [columns]
  );

  const colByField = useMemo(() => {
    const m = new Map();
    filterableCols.forEach((c) => m.set(c.field, c));
    return m;
  }, [filterableCols]);

  const [rows, setRows] = useState(() => {
    const initial = apiRef?.current?.getState?.()?.filter?.items || [];
    if (!initial.length) return [emptyRow()];
    return initial.map((it) => ({
      id: it.id || nextRowId(),
      field: it.field || "",
      operator: it.operator || "",
      value: it.value ?? "",
    }));
  });

  useEffect(() => {
    if (rows.length === 0) setRows([emptyRow()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live-commit the panel's rows to the grid's filterModel on every edit,
  // so the toolbar Search button (which reads filterModel) sees current state.
  // Skipped on mount to avoid wiping existing saved filters or triggering a refetch.
  const skipFirstCommit = useRef(true);
  useEffect(() => {
    if (skipFirstCommit.current) {
      skipFirstCommit.current = false;
      return;
    }
    const items = rows
      .filter((r) => r.field && r.operator)
      .filter((r) => {
        const col = colByField.get(r.field);
        const ops = operatorsFor(col);
        const op = ops.find((o) => o.value === r.operator);
        // Operators that don't need a value
        if (op && (op.value === "isempty" || op.value === "isnotempty" || op.value === "isEmpty" || op.value === "isNotEmpty")) return true;
        return r.value != null && r.value !== "";
      })
      .map((r) => ({ id: r.id, field: r.field, operator: r.operator, value: r.value }));
    apiRef?.current?.setFilterModel?.({ items, logicOperator: "and" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const updateRow = (id, patch) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const removeRow = (id) => setRows((prev) => (prev.length === 1 ? [emptyRow()] : prev.filter((r) => r.id !== id)));

  const clearAll = () => setRows([emptyRow()]);

  // Returns filterable operator options for a column.
  // Prefers the column's explicit filterOperators; falls back to MUI defaults by type.
  const operatorsFor = (col) => {
    if (!col) return [];
    let ops = col.filterOperators;
    if (!ops || ops.length === 0) {
      const colType = col.type || "string";
      if (colType === "number") {
        ops = getGridNumericOperators();
      } else if (colType === "date" || colType === "dateTime") {
        ops = getGridDateOperators(colType === "dateTime");
      } else if (colType === "boolean") {
        ops = getGridBooleanOperators();
      } else {
        ops = getGridStringOperators();
      }
    }
    return ops.map((o) => ({ value: o.value, label: o.label || o.headerName || o.value }));
  };

  const handleFieldChange = (id, field) => {
    const col = colByField.get(field);
    const ops = operatorsFor(col);
    const firstOp = ops[0]?.value || "";
    updateRow(id, { field, operator: firstOp, value: "" });
  };

  const handleApply = () => {
    const items = rows
      .filter((r) => r.field && r.operator)
      .filter((r) => {
        const col = colByField.get(r.field);
        const ops = operatorsFor(col);
        const op = ops.find((o) => o.value === r.operator);
        // Operators that don't need a value
        if (op && (op.value === "isempty" || op.value === "isnotempty" || op.value === "isEmpty" || op.value === "isNotEmpty")) return true;
        return r.value != null && r.value !== "";
      })
      .map((r) => ({ id: r.id, field: r.field, operator: r.operator, value: r.value }));

    console.log("[MultiFilterPanel.handleApply] rows=", JSON.stringify(rows), "items=", JSON.stringify(items));
    const model = { items, logicOperator: "and" };
    apiRef?.current?.setFilterModel?.(model);
    applyFiltersCallback?.(model);
    onClose?.();
  };

  return (
    <Box sx={{ p: 2, width: 460, maxWidth: "100%" }}>
      <Stack spacing={1.5}>
        {rows.map((row) => {
          const col = colByField.get(row.field);
          const operators = operatorsFor(col);
          const isAnyOf = row.operator === "isanyof";
          const valueOptions = typeof col?.valueOptions === "function" ? col.valueOptions() : col?.valueOptions;
          return (
            <Stack key={row.id} direction="row" spacing={1} alignItems="center">
              <FormControl size="small" sx={{ flex: 1.2 }}>
                <InputLabel>Field</InputLabel>
                <Select
                  label="Field"
                  value={row.field}
                  onChange={(e) => handleFieldChange(row.id, e.target.value)}
                >
                  {filterableCols.map((c) => (
                    <MenuItem key={c.field} value={c.field}>{c.headerName || c.field}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ flex: 1.4 }} disabled={!row.field}>
                <InputLabel>Operator</InputLabel>
                <Select
                  label="Operator"
                  value={row.operator}
                  onChange={(e) => updateRow(row.id, { operator: e.target.value, value: "" })}
                  MenuProps={{ PaperProps: { sx: { minWidth: 180 } } }}
                >
                  {operators.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ flex: 1.6 }}>
                {isAnyOf ? (
                  <MultiSelectValueEditor
                    value={row.value}
                    onChange={(v) => updateRow(row.id, { value: v })}
                    options={valueOptions}
                    label="Values"
                  />
                ) : (
                  <SingleValueEditor col={col} value={row.value} onChange={(v) => updateRow(row.id, { value: v })} />
                )}
              </Box>

              <IconButton size="small" onClick={() => removeRow(row.id)} aria-label="remove filter">
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </Stack>
          );
        })}

        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
          <Button size="small" onClick={addRow} disabled={filterableCols.length === 0}>
            + Add filter
          </Button>
          <Button size="small" color="inherit" onClick={clearAll}>Clear all</Button>
        </Stack>

        {rows.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            No filters. Click “Add filter”.
          </Typography>
        )}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="contained" size="small" onClick={handleApply}>Apply</Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default MultiFilterPanel;
